import { useEffect, useState, useSyncExternalStore } from 'react';
import { GamepadState } from './GamepadState';
import { ButtonGroup } from './ButtonGroup';
import { gamepadStore } from './store';
import { XInputValues } from '@playcastdotio/CoreTypes';
import { ButtonMeta } from './ButtonMeta';
import { Thumbstick } from './Thumbstick';
import { ButtonBumper } from './ButtonBumper';
import { ButtonTrigger } from './ButtonTrigger';
import { Button } from '@playcastdotio/Shadcn';
import { ButtonStick } from './ButtonStick';
import clsx from 'clsx';
import { useMobilePortrait } from './useMobilePortrait';
import { ButtonMenu } from './ButtonMenu';

interface MobileGamepadUiProps {
  testMode?: boolean;
  isPhysicalGamepadConnected?: boolean;
  disconnect?: () => void;
}

type ScreenState = 'standalone' | 'fullscreen' | 'browser';

export const MobileGamepadUi = (props: MobileGamepadUiProps) => {
  const { testMode } = props;
  const gamepad = useSyncExternalStore(gamepadStore.subscribeGamepad, gamepadStore.getGamepad);
  // @ts-expect-error: Property 'standalone' does not exist on type 'Navigator'
  const [status, setStatus] = useState<ScreenState>(window.navigator.standalone ? 'standalone' : 'browser');
  const isMobilePortrait = useMobilePortrait();
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      // const isIphoneSE = (screenWidth === 320 && screenHeight === 568) || (screenWidth === 568 && screenHeight === 320);
      const isIphone12Above = (screenWidth >= 390 && screenHeight >= 844) || (screenWidth >= 844 && screenHeight >= 390);

      setScale(isIphone12Above ? 1 : 0.75);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const goFullScreen = async () => {
    await document.body.requestFullscreen({ navigationUI: 'hide' });
    if (document.fullscreenElement) {
      setStatus('fullscreen');
    }
  };

  useEffect(() => {
    if (status === 'standalone') return;
    document.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement) {
        setStatus('fullscreen');
      } else {
        setStatus('browser');
      }
    });

    return () => {
      document.removeEventListener('fullscreenchange', () => {
        if (document.fullscreenElement) {
          setStatus('fullscreen');
        } else {
          setStatus('browser');
        }
      });
    };
  }, [status]);

  return (
    <div className='absolute z-[1000] flex h-dvh w-full items-end overflow-hidden'>
      <div
        className={clsx(
          isMobilePortrait
            ? 'right-0 top-full h-[100vw] w-[100dvh] origin-top-right rotate-90 overflow-hidden'
            : 'bottom-0 h-[100dvh] w-[100vw]',
          'absolute',
        )}
      >
        {!props.isPhysicalGamepadConnected && (
          <>
            {testMode && (
              <div className='h-max-full w-max-full absolute h-full w-full select-none'>
                <div className='flex h-full w-full justify-center p-6'>
                  <GamepadState gamepad={gamepad} />
                </div>
                <div className='absolute bottom-2 flex w-full flex-col gap-1 text-center text-white opacity-50'>
                  <div>
                    {status === 'browser' ? (
                      <Button onClick={goFullScreen} size='sm' className='m-0'>
                        Go Full Screen
                      </Button>
                    ) : status === 'standalone' ? (
                      'iOS Standalone'
                    ) : (
                      'Browser Full Screen'
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* bumper */}
            <div className={clsx(scale < 1 ? '-left-4 top-16' : 'left-0 top-20', `absolute`)}>
              <ButtonBumper prefix='left-' gamepad={gamepad} value={XInputValues.LEFT_SHOULDER} />
            </div>
            <div className={clsx(scale < 1 ? '-right-4 top-16' : 'right-0 top-20', `absolute`)}>
              <ButtonBumper prefix='right-' gamepad={gamepad} value={XInputValues.RIGHT_SHOULDER} />
            </div>

            {/* trigger */}
            <div className={clsx(scale < 1 ? '-left-4 top-2' : 'left-0 top-4', `absolute`)}>
              <ButtonTrigger prefix='left-' gamepad={gamepad} value={XInputValues.LEFT_TRIGGER} />
            </div>
            <div className={clsx(scale < 1 ? '-right-4 top-2' : 'right-0 top-4', `absolute`)}>
              <ButtonTrigger prefix='right-' gamepad={gamepad} value={XInputValues.RIGHT_TRIGGER} />
            </div>

            {/* dpad */}
            <div
              className={`absolute bottom-0 left-[17%]`}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              <div className='scale-75'>
                <ButtonGroup
                  prefix='dpad-'
                  gamepad={gamepad}
                  values={{
                    east: XInputValues.DPAD_RIGHT,
                    north: XInputValues.DPAD_UP,
                    south: XInputValues.DPAD_DOWN,
                    west: XInputValues.DPAD_LEFT,
                  }}
                />
              </div>
            </div>

            {/* face buttons */}
            <div
              className={`absolute bottom-[20%] right-4`}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              <ButtonGroup
                prefix='right-'
                gamepad={gamepad}
                values={{
                  east: XInputValues.B,
                  north: XInputValues.Y,
                  south: XInputValues.A,
                  west: XInputValues.X,
                }}
              />
            </div>

            {/* thumbsticks */}
            <div
              className={clsx(scale < 1 ? 'bottom-[15%]' : 'bottom-[20%]', `pointer-events-none absolute left-[6%] overflow-visible`)}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              <Thumbstick location='left' gamepad={gamepad} value={XInputValues.LEFT_THUMB} />
            </div>
            <div
              className={`pointer-events-none absolute bottom-0 right-[20%] overflow-visible`}
              style={{
                transform: `scale(${scale < 1 ? 0.6 : 0.75})`,
              }}
            >
              <Thumbstick location='right' gamepad={gamepad} value={XInputValues.RIGHT_THUMB} />
            </div>

            {/* meta buttons */}
            <div
              className={`absolute left-[20%] top-2`}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              <ButtonMeta prefix='left-' gamepad={gamepad} value={XInputValues.BACK} />
            </div>
            <div
              className={`absolute right-[20%] top-2`}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              <ButtonMeta prefix='right-' gamepad={gamepad} value={XInputValues.START} />
            </div>

            {/* button stick */}
            <div
              className={`absolute bottom-[20%] left-4`}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              <ButtonStick gamepad={gamepad} value={XInputValues.LEFT_THUMB} prefix='left-' />
            </div>
            <div
              className={`absolute bottom-0 right-[15%]`}
              style={{
                transform: `scale(${scale})`,
              }}
            >
              <ButtonStick gamepad={gamepad} value={XInputValues.RIGHT_THUMB} prefix='right-' />
            </div>
          </>
        )}

        {/* menu */}
        <div className='absolute bottom-0 left-1/2'>
          <ButtonMenu disconnect={props.disconnect} />
        </div>
      </div>
    </div>
  );
};
